import React, { useMemo, useRef } from "react";
import ReactECharts from "echarts-for-react";
import { useSelector } from "react-redux";
import { getLocationHierarchy } from "../../global/utility/widgetUtils";
import { toMin } from "../../global/utility/statisticsUtil";
import {
  CHART_FONT_FAMILY,
  TOOLTIP_STYLE,
  chartTextStyle,
  buildChartTitle,
  buildLegend,
  buildSliderDataZoom,
  SLIDER_LABEL_EVENTS,
  CHART_GRID_BOTTOM,
  buildYAxisNameGap,
  formatAxisValue,
  createChartToolbox,
  CHART_GRID_RIGHT_PADDING,
} from "./ApacheChartUtils";

const ApacheVerticalBarChart = ({
  title = "",
  xAxisData = [],
  tooltipXAxisData = [],
  series = [],
  height = "400px",
  loading = false,
}) => {
  const chartRef = useRef(null);
  const siteTagList = useSelector((state) => state.sitesReducer.siteTagList);
  const option = useMemo(() => {
    const locationIds = xAxisData?.locationIdData ?? [];
    const hasLocationIds = locationIds.some(Boolean);
    const xDataLocationHierarchy = (xAxisData?.data ?? []).map(
      (name, i) => (locationIds[i] && getLocationHierarchy(siteTagList, locationIds[i])) || name
    );
    const maxVal = series.reduce((max, s) => {
      const vals = (s?.data ?? []).filter((v) => v != null).map(Math.abs);
      return vals.length > 0 ? Math.max(max, Math.max(...vals)) : max;
    }, 0);
    const yAxisNameGap = buildYAxisNameGap(maxVal, xAxisData?.toolTipUnit);

    return {
      textStyle: chartTextStyle,
      title: buildChartTitle(title),
      tooltip: {
        trigger: "axis",
        axisPointer: { type: "none" },
        textStyle: { ...chartTextStyle, color: "#000000" },
        formatter: (params) => {
          const dataIndex = params[0]?.dataIndex;
          const label =
            tooltipXAxisData &&
            dataIndex >= 0 &&
            dataIndex < tooltipXAxisData.length &&
            tooltipXAxisData[dataIndex]
              ? tooltipXAxisData[dataIndex]
              : params[0]?.axisValue;
          const isMinutes = xAxisData?.toolTipUnit === " minute(s)";
          const isPercent = xAxisData?.toolTipUnit === "Percent";
          const formatValue = (val) => {
            if (isMinutes) return toMin(val);
            if (isPercent) return `${val}%`;
            return `${val}${xAxisData?.toolTipUnit ? ` ${xAxisData.toolTipUnit}` : ""}`;
          };
          const rows = params
            .map((p) => `<div style="${TOOLTIP_STYLE}">${p.marker}${p.seriesName}: <b>${formatValue(Math.abs(p.value))}</b></div>`)
            .join("");
          return `<div style="${TOOLTIP_STYLE}">${label}</div>${rows}`;
        },
      },
      legend: buildLegend(true),
      toolbox: createChartToolbox(chartRef),
      grid: {
        left: "5%",
        right: CHART_GRID_RIGHT_PADDING,
        bottom: CHART_GRID_BOTTOM,
        top: title ? 60 : 40,
        containLabel: true,
      },
      xAxis: {
        type: "category",
        data: xAxisData?.data,
        name: xAxisData?.xName || "",
        nameLocation: "middle",
        nameGap: 25,
        nameTextStyle: { fontWeight: "bold" },
        axisLabel: { rotate: 0, fontFamily: CHART_FONT_FAMILY, fontSize: 12 },
        splitLine: { show: false },
        ...(hasLocationIds && {
          triggerEvent: true,
          tooltip: {
            show: true,
            textStyle: { ...chartTextStyle, color: "#000000" },
            formatter: (params) =>
              `<div style="${TOOLTIP_STYLE}">${xDataLocationHierarchy[params?.tickIndex] ?? params?.name ?? ""}</div>`,
          },
        }),
      },
      yAxis: {
        type: "value",
        minInterval: 1,
        name: xAxisData?.yName || "",
        nameLocation: "middle",
        nameGap: yAxisNameGap,
        nameRotate: 90,
        nameTextStyle: { fontWeight: "bold" },
        axisLabel: {
          fontFamily: CHART_FONT_FAMILY,
          fontSize: 12,
          formatter: (val) => formatAxisValue(val, xAxisData?.toolTipUnit),
        },
        splitLine: { show: true },
      },
      dataZoom: buildSliderDataZoom(0, "x"),
      series: series.map((s) => ({
        name: s?.name,
        type: "bar",
        data: s?.data,
        color: s?.color ?? "#0089EC",
        barMaxWidth: 20,
        itemStyle: { borderRadius: [4, 4, 0, 0] },
        emphasis: { focus: "series" },
        label: {
          show: s?.showLabel ?? false,
          position: "top",
          fontFamily: CHART_FONT_FAMILY,
          fontSize: 12,
          color: "#000000",
        },
      })),
    };
  }, [title, xAxisData, tooltipXAxisData, series, siteTagList]);

  return (
    <ReactECharts
      ref={chartRef}
      option={option}
      style={{ height }}
      showLoading={loading}
      notMerge
      lazyUpdate
      onEvents={SLIDER_LABEL_EVENTS}
    />
  );
};

export default ApacheVerticalBarChart;
